/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { QuestionnaireResponseStatus, UserRole } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireResponseProps {
  id: string;
  status: QuestionnaireResponseStatus;
  currentReviewLevel: UserRole | null;
  questionnaireId: string;
  completedById: string | null;
  dateCompleted: Date | null;
  communicationsReviewById: string | null;
  dateCommunicationsReviewed: Date | null;
  supervisorReviewById: string | null;
  dateSupervisorReviewed: Date | null;
  managerReviewById: string | null;
  dateManagerReviewed: Date | null;
  companyId: string;
  cardId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireResponseRecord extends Record<QuestionnaireResponseProps>({
  id: null as any,
  status: null as any,
  currentReviewLevel: null as any,
  questionnaireId: null as any,
  completedById: null as any,
  dateCompleted: null as any,
  communicationsReviewById: null as any,
  dateCommunicationsReviewed: null as any,
  supervisorReviewById: null as any,
  dateSupervisorReviewed: null as any,
  managerReviewById: null as any,
  dateManagerReviewed: null as any,
  companyId: null as any,
  cardId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: QuestionnaireResponseStatus;
    currentReviewLevel: UserRole | null;
    questionnaireId: string;
    completedById: string | null;
    dateCompleted: string | number | Date | null;
    communicationsReviewById: string | null;
    dateCommunicationsReviewed: string | number | Date | null;
    supervisorReviewById: string | null;
    dateSupervisorReviewed: string | number | Date | null;
    managerReviewById: string | null;
    dateManagerReviewed: string | number | Date | null;
    companyId: string;
    cardId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireResponseRecord {
    if (json == null) {
      throw new Error('QuestionnaireResponseRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireResponseRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireResponseJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', QuestionnaireResponseStatus.NotStarted),
        'QuestionnaireResponseJson.status',
      ),
      currentReviewLevel: json.currentReviewLevel,
      questionnaireId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'questionnaireId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireResponseJson.questionnaireId',
      ),
      completedById: json.completedById,
      dateCompleted: json.dateCompleted != null ? new Date(json.dateCompleted) : null,
      communicationsReviewById: json.communicationsReviewById,
      dateCommunicationsReviewed:
        json.dateCommunicationsReviewed != null ? new Date(json.dateCommunicationsReviewed) : null,
      supervisorReviewById: json.supervisorReviewById,
      dateSupervisorReviewed:
        json.dateSupervisorReviewed != null ? new Date(json.dateSupervisorReviewed) : null,
      managerReviewById: json.managerReviewById,
      dateManagerReviewed:
        json.dateManagerReviewed != null ? new Date(json.dateManagerReviewed) : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireResponseJson.companyId',
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireResponseJson.cardId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: QuestionnaireResponseStatus;
    currentReviewLevel: UserRole | null;
    questionnaireId: string;
    completedById: string | null;
    dateCompleted: string | number | Date | null;
    communicationsReviewById: string | null;
    dateCommunicationsReviewed: string | number | Date | null;
    supervisorReviewById: string | null;
    dateSupervisorReviewed: string | number | Date | null;
    managerReviewById: string | null;
    dateManagerReviewed: string | number | Date | null;
    companyId: string;
    cardId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireResponseRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionnaireResponseRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionnaireResponseRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireResponseJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', QuestionnaireResponseStatus.NotStarted),
            'QuestionnaireResponseJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentReviewLevel: tryOrDefault(
        () => json.currentReviewLevel,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'questionnaireId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireResponseJson.questionnaireId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      completedById: tryOrDefault(
        () => json.completedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCompleted: tryOrDefault(
        () => (json.dateCompleted != null ? new Date(json.dateCompleted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationsReviewById: tryOrDefault(
        () => json.communicationsReviewById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCommunicationsReviewed: tryOrDefault(
        () =>
          json.dateCommunicationsReviewed != null
            ? new Date(json.dateCommunicationsReviewed)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      supervisorReviewById: tryOrDefault(
        () => json.supervisorReviewById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateSupervisorReviewed: tryOrDefault(
        () => (json.dateSupervisorReviewed != null ? new Date(json.dateSupervisorReviewed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      managerReviewById: tryOrDefault(
        () => json.managerReviewById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateManagerReviewed: tryOrDefault(
        () => (json.dateManagerReviewed != null ? new Date(json.dateManagerReviewed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireResponseJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireResponseJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireResponseProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionnaireResponseRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionnaireResponseViewModelBase implements QuestionnaireResponseProps {
  protected readonly _props: QuestionnaireResponseProps;
  private _json?: QuestionnaireResponseProps;

  get id(): string {
    return this._props.id;
  }
  get status(): QuestionnaireResponseStatus {
    return this._props.status;
  }
  get currentReviewLevel(): UserRole | null {
    return this._props.currentReviewLevel;
  }
  get questionnaireId(): string {
    return this._props.questionnaireId;
  }
  get completedById(): string | null {
    return this._props.completedById;
  }
  get dateCompleted(): Date | null {
    return this._props.dateCompleted;
  }
  get communicationsReviewById(): string | null {
    return this._props.communicationsReviewById;
  }
  get dateCommunicationsReviewed(): Date | null {
    return this._props.dateCommunicationsReviewed;
  }
  get supervisorReviewById(): string | null {
    return this._props.supervisorReviewById;
  }
  get dateSupervisorReviewed(): Date | null {
    return this._props.dateSupervisorReviewed;
  }
  get managerReviewById(): string | null {
    return this._props.managerReviewById;
  }
  get dateManagerReviewed(): Date | null {
    return this._props.dateManagerReviewed;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get cardId(): string {
    return this._props.cardId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAwaitingReview() {
    return this._props.status === QuestionnaireResponseStatus.AwaitingReview;
  }
  public get isCompleted() {
    return this._props.status === QuestionnaireResponseStatus.Completed;
  }
  public get isDraft() {
    return this._props.status === QuestionnaireResponseStatus.Draft;
  }
  public get isNotStarted() {
    return this._props.status === QuestionnaireResponseStatus.NotStarted;
  }
  public get isAsbDataManager() {
    return this._props.currentReviewLevel === UserRole.AsbDataManager;
  }
  public get isAsbViewer() {
    return this._props.currentReviewLevel === UserRole.AsbViewer;
  }
  public get isAviationDataManager() {
    return this._props.currentReviewLevel === UserRole.AviationDataManager;
  }
  public get isAviationPermissionsManager() {
    return this._props.currentReviewLevel === UserRole.AviationPermissionsManager;
  }
  public get isAviationSupervisor() {
    return this._props.currentReviewLevel === UserRole.AviationSupervisor;
  }
  public get isBiller() {
    return this._props.currentReviewLevel === UserRole.Biller;
  }
  public get isBillerCorrectionsTeam() {
    return this._props.currentReviewLevel === UserRole.BillerCorrectionsTeam;
  }
  public get isClinical() {
    return this._props.currentReviewLevel === UserRole.Clinical;
  }
  public get isCommunications() {
    return this._props.currentReviewLevel === UserRole.Communications;
  }
  public get isCommunicationsSupervisor() {
    return this._props.currentReviewLevel === UserRole.CommunicationsSupervisor;
  }
  public get isComplianceManager() {
    return this._props.currentReviewLevel === UserRole.ComplianceManager;
  }
  public get isDeveloper() {
    return this._props.currentReviewLevel === UserRole.Developer;
  }
  public get isExecutive() {
    return this._props.currentReviewLevel === UserRole.Executive;
  }
  public get isExternal() {
    return this._props.currentReviewLevel === UserRole.External;
  }
  public get isFixedWingCoordinator() {
    return this._props.currentReviewLevel === UserRole.FixedWingCoordinator;
  }
  public get isFleetStatusOperations() {
    return this._props.currentReviewLevel === UserRole.FleetStatusOperations;
  }
  public get isFleetStatusViewer() {
    return this._props.currentReviewLevel === UserRole.FleetStatusViewer;
  }
  public get isFlightLogViewer() {
    return this._props.currentReviewLevel === UserRole.FlightLogViewer;
  }
  public get isGlobalDataManager() {
    return this._props.currentReviewLevel === UserRole.GlobalDataManager;
  }
  public get isGlobalPermissionsManager() {
    return this._props.currentReviewLevel === UserRole.GlobalPermissionsManager;
  }
  public get isImageTrend() {
    return this._props.currentReviewLevel === UserRole.ImageTrend;
  }
  public get isMechanic() {
    return this._props.currentReviewLevel === UserRole.Mechanic;
  }
  public get isOCC() {
    return this._props.currentReviewLevel === UserRole.OCC;
  }
  public get isOccReader() {
    return this._props.currentReviewLevel === UserRole.OccReader;
  }
  public get isOosMaintenanceController() {
    return this._props.currentReviewLevel === UserRole.OosMaintenanceController;
  }
  public get isOosQA() {
    return this._props.currentReviewLevel === UserRole.OosQA;
  }
  public get isOosViewer() {
    return this._props.currentReviewLevel === UserRole.OosViewer;
  }
  public get isOperationsDataManager() {
    return this._props.currentReviewLevel === UserRole.OperationsDataManager;
  }
  public get isOperationsPermissionsManager() {
    return this._props.currentReviewLevel === UserRole.OperationsPermissionsManager;
  }
  public get isOpsReader() {
    return this._props.currentReviewLevel === UserRole.OpsReader;
  }
  public get isOrgReader() {
    return this._props.currentReviewLevel === UserRole.OrgReader;
  }
  public get isPilot() {
    return this._props.currentReviewLevel === UserRole.Pilot;
  }
  public get isPrdAdmin() {
    return this._props.currentReviewLevel === UserRole.PrdAdmin;
  }
  public get isPrdUser() {
    return this._props.currentReviewLevel === UserRole.PrdUser;
  }
  public get isProgramManager() {
    return this._props.currentReviewLevel === UserRole.ProgramManager;
  }
  public get isQaManager() {
    return this._props.currentReviewLevel === UserRole.QaManager;
  }
  public get isQaSupervisor() {
    return this._props.currentReviewLevel === UserRole.QaSupervisor;
  }
  public get isRemoteUser() {
    return this._props.currentReviewLevel === UserRole.RemoteUser;
  }
  public get isStakeholder() {
    return this._props.currentReviewLevel === UserRole.Stakeholder;
  }
  public get isSupport() {
    return this._props.currentReviewLevel === UserRole.Support;
  }
  public get isTrmProgramAdmin() {
    return this._props.currentReviewLevel === UserRole.TrmProgramAdmin;
  }

  public isAnyStatus(...questionnaireResponseStatus: QuestionnaireResponseStatus[]) {
    return questionnaireResponseStatus.includes(this._props.status);
  }
  public isAnyCurrentReviewLevel(...userRole: UserRole[]) {
    return (
      this._props.currentReviewLevel != null && userRole.includes(this._props.currentReviewLevel)
    );
  }

  constructor(props: QuestionnaireResponseProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        currentReviewLevel: this._props.currentReviewLevel,
        questionnaireId: this._props.questionnaireId,
        completedById: this._props.completedById,
        dateCompleted: this._props.dateCompleted,
        communicationsReviewById: this._props.communicationsReviewById,
        dateCommunicationsReviewed: this._props.dateCommunicationsReviewed,
        supervisorReviewById: this._props.supervisorReviewById,
        dateSupervisorReviewed: this._props.dateSupervisorReviewed,
        managerReviewById: this._props.managerReviewById,
        dateManagerReviewed: this._props.dateManagerReviewed,
        companyId: this._props.companyId,
        cardId: this._props.cardId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireResponseEntities = (state: AppState) =>
  state.entityStores.questionnaireResponse.entities;

export const selectQuestionnaireResponseById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireResponse.entities.get(id);

export const selectQuestionnaireResponseMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireResponse.meta.get(id);

export const selectQuestionnaireResponseAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireResponseById(id),
    selectQuestionnaireResponseMetadataById(id),
    (questionnaireResponse, meta) => ({ questionnaireResponse, meta }),
  );

export function selectQuestionnaireResponseEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireResponseEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireResponseRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireResponseEntitiesByStatus(status: QuestionnaireResponseStatus) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByCurrentReviewLevel(
  currentReviewLevel: UserRole,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.currentReviewLevel === currentReviewLevel).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByQuestionnaireId(questionnaireId: string) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.questionnaireId === questionnaireId).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByCompletedById(completedById: string) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.completedById === completedById).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByDateCompleted(dateCompleted: Date) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.dateCompleted === dateCompleted).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByCommunicationsReviewById(
  communicationsReviewById: string,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.communicationsReviewById === communicationsReviewById).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByDateCommunicationsReviewed(
  dateCommunicationsReviewed: Date,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.dateCommunicationsReviewed === dateCommunicationsReviewed).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesBySupervisorReviewById(
  supervisorReviewById: string,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.supervisorReviewById === supervisorReviewById).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByDateSupervisorReviewed(
  dateSupervisorReviewed: Date,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.dateSupervisorReviewed === dateSupervisorReviewed).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByManagerReviewById(managerReviewById: string) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.managerReviewById === managerReviewById).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByDateManagerReviewed(
  dateManagerReviewed: Date,
) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.dateManagerReviewed === dateManagerReviewed).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectQuestionnaireResponseEntitiesByCardId(cardId: string) {
  return createSelector(selectAllQuestionnaireResponseEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}
//#endregion Selectors
